import React, {useState, useEffect} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import usePort from "../hooks/usePort";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import PortTypesListDropdownComponent from "../common/port-types-list-dropdown.component";


const EditPortComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {portDetails, viewPort, isUpdatingPort, updatePort, isFetchingPortType, portTypeList, getPortTypeList} = usePort();

    const [portNameInput, setPortNameInput] = useState("");
    const [selectedPortType, setSelectedPortType] = useState(null);
    const [locationInput, setLocationInput] = useState("");
    const [descriptionInput, setDescriptionInput] = useState("");
    const [latitudeInput, setLatitudeInput] = useState("");
    const [longitudeInput, setLongitudeInput] = useState(""); 
    
    const portId = new URLSearchParams(location.search).get("id");


    useEffect(() => {
        const parameters = {portUID: portId};
                            
        viewPort(parameters);
    }, [portId]);

    useEffect(() => {
        if (portDetails) {
            setPortNameInput(portDetails?.port_name);
            setSelectedPortType(portDetails?.type);
            setLocationInput(portDetails?.location);
            setDescriptionInput(portDetails?.description);
            setLatitudeInput(portDetails?.latitude);
            setLongitudeInput(portDetails?.longitude);
        }
    }, [portDetails])


    const handlePortTypeChange = (value) => {
        setSelectedPortType(value);
    }

    const handleTextfieldChange = (e, type) => {
        const {value} = e.target;

        switch (type) {
            case "portName":
                setPortNameInput(value);
                break;

            case "location":
                setLocationInput(value);
                break;

            case "description":
                setDescriptionInput(value);
                break;

            case "latitude":
                setLatitudeInput(value);
                break;

            case "longitude":
                setLongitudeInput(value);
                break;

            default:
                break;
        }
    }

    const handleBack = () => {
        navigate("/ports"); 
    }

    const handleUpdatePort = () => {
        const payload = {
            port_uid: portId,
            port_name: portNameInput,
            type: selectedPortType,
            location: locationInput,
            description: descriptionInput,
            latitude: latitudeInput,
            longitude: longitudeInput,
            status: portDetails?.status,
        }

        updatePort(payload);
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Port List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Edit Port"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Update the details of the port for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Port Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to update port"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack spacing={4}>
                        <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Port Name"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                            
                                <CustomTextField 
                                    placeholder={"Enter port name"}
                                    variant={"outlined"}
                                    value={portNameInput}
                                    onChange={(e) => handleTextfieldChange(e, "portName")}
                                    fullWidth={true}
                                    allowedPattern={/[^a-zA-Z0-9\s]/g}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isUpdatingPort}
                                />           
                            </Stack>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Port Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <PortTypesListDropdownComponent 
                                    value={selectedPortType}
                                    onChange={handlePortTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isUpdatingPort}
                                    isFetchingPortType={isFetchingPortType} 
                                    portTypeList={portTypeList}
                                    getPortTypeList={getPortTypeList}
                                />
                            </Stack>
                        </Stack>

                        <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Location"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <CustomTextField 
                                    placeholder={"Enter location"}
                                    variant={"outlined"}
                                    value={locationInput}
                                    onChange={(e) => handleTextfieldChange(e, "location")}
                                    fullWidth={true}
                                    allowedPattern={/[^a-zA-Z0-9\s,.'\-\n]/gi}
                                    multiline={true}
                                    hiddenLabel
                                    readOnly={isUpdatingPort}
                                    rows={2}
                                />   
                            </Stack>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Description"}
                                    customStyles={{
                                         marginBottom: 1, 
                                    }}
                                />
                                <CustomTextField 
                                    placeholder={"Enter description"}
                                    variant={"outlined"}
                                    value={descriptionInput}
                                    onChange={(e) => handleTextfieldChange(e, "description")}
                                    fullWidth={true}
                                    multiline={true}
                                    hiddenLabel
                                    readOnly={isUpdatingPort}
                                    rows={2}
                                />   
                            </Stack>
                        </Stack>

                        <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Latitude"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <CustomTextField 
                                    placeholder={"Enter latitude"}
                                    variant={"outlined"}
                                    value={latitudeInput}
                                    onChange={(e) => handleTextfieldChange(e, "latitude")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9\s,.'\-\n]/gi}
                                    hiddenLabel
                                    readOnly={isUpdatingPort}
                                />    
                            </Stack>
                            <Stack spacing={1} flex={1}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Longitude"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <CustomTextField 
                                    placeholder={"Enter longitude"}
                                    variant={"outlined"}
                                    value={longitudeInput}
                                    onChange={(e) => handleTextfieldChange(e, "longitude")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9\s,.'\-\n]/gi}
                                    hiddenLabel
                                    readOnly={isUpdatingPort}
                                />    
                            </Stack>
                        </Stack>   
                    </Stack> 

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isUpdatingPort) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Update Port"}
                            startIcon={isUpdatingPort && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (portNameInput && selectedPortType && locationInput && descriptionInput && !isUpdatingPort &&
                                    (   
                                        portNameInput !== portDetails?.port_name || selectedPortType !== portDetails?.type || 
                                        locationInput !== portDetails?.location || descriptionInput !== portDetails?.description
                                    )
                                )  ? "100%" : "55%",
                                cursor: (portNameInput && selectedPortType && locationInput && descriptionInput && !isUpdatingPort &&
                                    ( 
                                        portNameInput !== portDetails?.port_name || selectedPortType !== portDetails?.type || 
                                        locationInput !== portDetails?.location || descriptionInput !== portDetails?.description
                                    )
                                ) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={
                                (portNameInput && selectedPortType && locationInput && descriptionInput && !isUpdatingPort &&
                                    ( 
                                        portNameInput !== portDetails?.port_name || selectedPortType !== portDetails?.type || 
                                        locationInput !== portDetails?.location || descriptionInput !== portDetails?.description
                                    )
                                ) ? handleUpdatePort : null
                            }
                        />    
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default EditPortComponent